'use client';

/**
 * Context for context-aware drawer: modules (e.g. chat) can register extra content
 * (e.g. thread list) to show in the shell drawer when on their route. One burger only.
 * @see docs/UX_GUIDELINES.md §7, docs/DESIGN_MIGRATION_PLAN.md Phase 1.3, docs/mockups/two-burger-ux-states.html
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useState } from 'react';

export type DrawerExtraContextValue = {
  extraContent: ReactNode;
  setExtraContent: (node: ReactNode) => void;
  closeDrawer: () => void;
  setCloseDrawer: (fn: () => void) => void;
};

const defaultClose = (): void => {};

const defaultValue: DrawerExtraContextValue = {
  extraContent: null,
  setExtraContent: () => {},
  closeDrawer: defaultClose,
  setCloseDrawer: () => {},
};

export const DrawerExtraContext = createContext<DrawerExtraContextValue>(defaultValue);

export function DrawerExtraProvider({ children }: { children: ReactNode }) {
  const [extraContent, setExtraContent] = useState<ReactNode>(null);
  const [closeDrawerFn, setCloseDrawerFn] = useState<() => void>(() => defaultClose);
  const closeDrawer = useCallback(() => {
    closeDrawerFn();
  }, [closeDrawerFn]);
  const setCloseDrawer = useCallback((fn: () => void) => {
    setCloseDrawerFn(fn);
  }, []);
  const value: DrawerExtraContextValue = {
    extraContent,
    setExtraContent,
    closeDrawer,
    setCloseDrawer,
  };
  return (
    <DrawerExtraContext.Provider value={value}>
      {children}
    </DrawerExtraContext.Provider>
  );
}
